import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CloneScenarioDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newScenarioName: string) => void;
  originalScenarioName?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function CloneScenarioDialog({
  isOpen,
  onClose,
  onConfirm,
  originalScenarioName = "Scenario",
  onCloseAutoFocus,
}: CloneScenarioDialogProps) {
  const [newScenarioName, setNewScenarioName] = useState(`${originalScenarioName} (clone)`);

  useEffect(() => {
    if (isOpen) {
      setNewScenarioName(`${originalScenarioName} (clone)`);
    }
  }, [originalScenarioName, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newScenarioName.trim()) {
      onConfirm(newScenarioName.trim());
      setNewScenarioName(`${originalScenarioName} (clone)`);
      onClose();
    }
  };

  const handleClose = () => {
    setNewScenarioName(`${originalScenarioName} (clone)`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle className="text-white">Clone Scenario</DialogTitle>
          <DialogDescription className="sr-only">
            Enter a name for the cloned scenario and confirm to create a copy.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="cloneName" className="text-white">
              New Scenario Name
            </Label>
            <Input
              id="cloneName"
              value={newScenarioName}
              onChange={(e) => setNewScenarioName(e.target.value)}
              className="mt-2 bg-surface-input border-surface-border text-white placeholder:text-muted focus:ring-primary"
              placeholder="Enter new scenario name"
              autoFocus
            />
          </div>
          <DialogFooter>
            {/* Route Cancel through Radix's Close primitive — see
                clone-persona-dialog for the DEF-792-02 rationale. */}
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!newScenarioName.trim()}
            >
              Clone Scenario
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}